import {FilterDescriptor, process} from '@progress/kendo-data-query';
import {Button} from '@progress/kendo-react-buttons';
import {DropDownList} from '@progress/kendo-react-dropdowns';
import React from 'react';
import {AddOutline, TrashOutline} from '../../../../../icons/icons';
import {airportFieldsOptions, AirportTooltipFieldSelection} from '../../../../../static/static-airport-tooltip-fields';
import {AirportSelection} from '../../../../../common/enums';
import {store} from '../../../../state/store';
import {useAppDispatch} from '../../../../hooks/store-hooks';
import {debounce} from '@fe/core';
import {setSetting} from '../../../../state/slices/settings-slice';
import MarkerText from '../../../../components/inputs/MarkerText';
import {useSearchContext} from '../../context/SettingsSearchContext';

const MAX_ITEM_COUNT = 10;
export default function SettingsAirportTooltipsGroup() {
    const dispatch = useAppDispatch();
    const context = useSearchContext();
    const [fields, setFields] = React.useState(store.getState().settings.airportTooltipFields);
    const maxCountReached = fields.length >= MAX_ITEM_COUNT;

    const saveChanges = React.useCallback(
        debounce((fields: AirportTooltipFieldSelection[]) => {
            dispatch(
                setSetting(
                    'airportTooltipFields',
                    fields.filter((x) => x !== AirportSelection.None)
                )
            );
        }, 1000),
        []
    );

    const handleAddNewItem = () => {
        if (maxCountReached) {
            return;
        }
        setFields([...fields, AirportSelection.None]);
    };

    const handleDelete = (i: number) => {
        const newFields = fields.toSpliced(i, 1);
        setFields(newFields);
        saveChanges(newFields);
    };

    const handleChange = (i: number, value: AirportTooltipFieldSelection) => {
        const newFields = [...fields.slice(0, i), value, ...fields.slice(i + 1)];
        setFields(newFields);
        saveChanges(newFields);
    };

    return (
        <div className="px-3 py-2 d-flex flex-column gap-2">
            <div className="d-flex">
                <div className="d-flex align-items-center">
                    <MarkerText highlight={context.term}>{`Fields (max ${MAX_ITEM_COUNT})`}</MarkerText>
                </div>
                <div className="flex-grow-1"></div>
                <Button svgIcon={AddOutline} themeColor={'primary'} onClick={handleAddNewItem}
                        disabled={maxCountReached}/>
            </div>
            <div className="d-flex flex-column gap-2">
                {fields.map((x, i) => (
                    <TooltipItem key={i} i={i} value={x} onDelete={handleDelete} onChange={handleChange}/>
                ))}
            </div>
        </div>
    );
}

function TooltipItem({
                         i,
                         value,
                         onDelete,
                         onChange
                     }: {
    i: number;
    value: AirportTooltipFieldSelection;
    onDelete: (i: number) => void;
    onChange: (i: number, value: AirportTooltipFieldSelection) => void;
}) {
    const valueObject = React.useMemo(() => airportFieldsOptions.find((x) => x.value === value), [value]);
    const [filter, setFilter] = React.useState<FilterDescriptor>();
    const filtered = React.useMemo(
        () =>
            filter ? process(airportFieldsOptions, {
                filter: {
                    logic: 'or',
                    filters: [filter]
                }
            }).data : airportFieldsOptions,
        [filter]
    );

    return (
        <div className="d-flex gap-2 align-items-center">
            <div style={{width: '2ch'}}>{i + 1}.</div>

            <DropDownList
                filterable={true}
                textField="text"
                onFilterChange={(e) => setFilter(e.filter)}
                className="flex-grow-1"
                value={valueObject}
                data={filtered}
                onChange={(e) => onChange(i, (e.value as NonNullable<typeof valueObject>).value)}
                onFocus={() => setFilter(undefined)}
            />
            <Button svgIcon={TrashOutline} fillMode={'outline'} themeColor={'error'} onClick={() => onDelete(i)}/>
        </div>
    );
}
